import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import dayjs from 'dayjs';
import { t } from 'ember-intl';
import {
  STAY_DATE_GRANULARITIES,
  type StayDateGranularity,
} from 'lumen/utils/constants/net-revenue-constants';
import { calculateRelativeChange } from 'lumen/utils/net-revenue/metrics';
import type { StayDatePoint } from 'lumen/utils/net-revenue/types';
import { deltaColorClass } from 'lumen/utils/net-revenue/variance';

import styles from './tooltip.module.css';

interface MetricDifference {
  colorClass: string;
  formatted: string;
}

interface HotelNetRevenueStayDateChartTooltipSignature {
  Args: {
    comparisonLabel: string | null;
    currencyCode: string;
    granularity: StayDateGranularity;
    point: StayDatePoint;
  };
}

export default class HotelNetRevenueStayDateChartTooltip extends Component<HotelNetRevenueStayDateChartTooltipSignature> {
  @service declare intl: Services['intl'];

  get costDifference(): MetricDifference {
    // Cost inverts colours — a higher cost is "bad" (critical), matching the
    // cost-drilldown table.
    return this.#buildMetricDifference(
      this.args.point.cost,
      this.args.point.comparison?.cost,
      true,
    );
  }

  get formattedComparisonCost(): string {
    return this.#formatCurrency(this.args.point.comparison?.cost);
  }

  get formattedComparisonNetRevenue(): string {
    return this.#formatCurrency(this.args.point.comparison?.netRevenue);
  }

  get formattedComparisonOccupancy(): string {
    const value = this.args.point.comparison?.occupancy;
    return value === undefined
      ? this.intl.t('general.missing')
      : this.#formatPercent(value);
  }

  get formattedComparisonRoomRevenue(): string {
    return this.#formatCurrency(this.args.point.comparison?.roomRevenue);
  }

  get formattedCost(): string {
    return this.#formatCurrency(this.args.point.cost);
  }

  get formattedNetRevenue(): string {
    return this.#formatCurrency(this.args.point.netRevenue);
  }

  get formattedOccupancy(): string {
    return this.#formatPercent(this.args.point.occupancy);
  }

  get formattedRoomRevenue(): string {
    return this.#formatCurrency(this.args.point.roomRevenue);
  }

  get formattedTitle(): string {
    const date = dayjs(this.args.point.label);
    if (this.args.granularity === STAY_DATE_GRANULARITIES.WEEK) {
      return this.intl.t('general.week_of', { date: date.format('D MMM') });
    }
    if (this.args.granularity === STAY_DATE_GRANULARITIES.MONTH) {
      return date.format('MMM YYYY');
    }
    return date.format('dddd DD MMMM YYYY');
  }

  get netRevenueDifference(): MetricDifference {
    return this.#buildMetricDifference(
      this.args.point.netRevenue,
      this.args.point.comparison?.netRevenue,
      false,
    );
  }

  get occupancyDifference(): MetricDifference {
    return this.#buildMetricDifference(
      this.args.point.occupancy,
      this.args.point.comparison?.occupancy,
      false,
    );
  }

  get roomRevenueDifference(): MetricDifference {
    return this.#buildMetricDifference(
      this.args.point.roomRevenue,
      this.args.point.comparison?.roomRevenue,
      false,
    );
  }

  get showComparison(): boolean {
    return this.args.comparisonLabel !== null;
  }

  /**
   * Relative change of a metric vs its comparison, formatted as a signed
   * percent with a colour class. `invertColors` flips success/critical for
   * "lower is better" metrics (cost). Returns "—" / neutral when there is no
   * comparable value.
   */
  #buildMetricDifference(
    current: number,
    comparison: number | undefined,
    invertColors: boolean,
  ): MetricDifference {
    const change = calculateRelativeChange(current, comparison);
    if (change === null) {
      return {
        formatted: this.intl.t('general.missing'),
        colorClass: 'text-neutral-default',
      };
    }
    return {
      formatted: this.intl.formatNumber(change, {
        style: 'percent',
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
        signDisplay: 'exceptZero',
      }),
      // 0.0005 becomes 0.05%, and rounds to "0.0%" at display precision → neutral.
      colorClass: deltaColorClass(change, {
        invert: invertColors,
        neutralThreshold: 0.0005,
      }),
    };
  }

  #formatCurrency(value: number | undefined): string {
    if (value === undefined) {
      return this.intl.t('general.missing');
    }
    return this.intl.formatNumber(value, {
      style: 'currency',
      currency: this.args.currencyCode,
      currencyDisplay: 'symbol',
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    });
  }

  #formatPercent(value: number): string {
    return this.intl.formatNumber(value, {
      style: 'percent',
      maximumFractionDigits: 1,
      minimumFractionDigits: 0,
    });
  }

  <template>
    <div class={{styles.tooltip}} data-test-id="stay-date-chart-tooltip">
      <div class={{styles.title}} data-test-id="stay-date-chart-tooltip-title">
        {{this.formattedTitle}}
      </div>
      <table class={{styles.table}}>
        <thead>
          <tr>
            <th
              class={{styles.header-secondary}}
              data-test-id="stay-date-chart-tooltip-property-header"
            >
              {{t "net_revenue.tooltip_property"}}
            </th>
            <th class={{styles.header}}>
              {{t "general.current_period"}}
            </th>
            {{#if this.showComparison}}
              <th
                class={{styles.header-secondary}}
                data-test-id="stay-date-chart-tooltip-comparison-header"
              >
                {{@comparisonLabel}}
              </th>
              <th
                class={{styles.header-secondary}}
                data-test-id="stay-date-chart-tooltip-difference-header"
              >
                {{t "general.difference"}}
              </th>
            {{/if}}
          </tr>
        </thead>
        <tbody>
          <tr data-test-id="stay-date-chart-tooltip-row-net-revenue">
            <th class={{styles.label}}>{{t "net_revenue.net_revenue"}}</th>
            <td class={{styles.value}}>{{this.formattedNetRevenue}}</td>
            {{#if this.showComparison}}
              <td class={{styles.value-secondary}}>
                {{this.formattedComparisonNetRevenue}}
              </td>
              <td
                class="{{styles.value-secondary}}
                  {{this.netRevenueDifference.colorClass}}"
                data-test-id="stay-date-chart-tooltip-difference-net-revenue"
              >
                {{this.netRevenueDifference.formatted}}
              </td>
            {{/if}}
          </tr>
          <tr data-test-id="stay-date-chart-tooltip-row-cost">
            <th class={{styles.label}}>{{t "net_revenue.cost"}}</th>
            <td class={{styles.value}}>{{this.formattedCost}}</td>
            {{#if this.showComparison}}
              <td class={{styles.value-secondary}}>
                {{this.formattedComparisonCost}}
              </td>
              <td
                class="{{styles.value-secondary}}
                  {{this.costDifference.colorClass}}"
                data-test-id="stay-date-chart-tooltip-difference-cost"
              >
                {{this.costDifference.formatted}}
              </td>
            {{/if}}
          </tr>
          <tr data-test-id="stay-date-chart-tooltip-row-room-revenue">
            <th class={{styles.label}}>{{t "net_revenue.room_revenue"}}</th>
            <td class={{styles.value}}>{{this.formattedRoomRevenue}}</td>
            {{#if this.showComparison}}
              <td class={{styles.value-secondary}}>
                {{this.formattedComparisonRoomRevenue}}
              </td>
              <td
                class="{{styles.value-secondary}}
                  {{this.roomRevenueDifference.colorClass}}"
                data-test-id="stay-date-chart-tooltip-difference-room-revenue"
              >
                {{this.roomRevenueDifference.formatted}}
              </td>
            {{/if}}
          </tr>
          <tr data-test-id="stay-date-chart-tooltip-row-occupancy">
            <th class={{styles.label}}>{{t "general.occupancy"}}</th>
            <td class={{styles.value}}>{{this.formattedOccupancy}}</td>
            {{#if this.showComparison}}
              <td class={{styles.value-secondary}}>
                {{this.formattedComparisonOccupancy}}
              </td>
              <td
                class="{{styles.value-secondary}}
                  {{this.occupancyDifference.colorClass}}"
                data-test-id="stay-date-chart-tooltip-difference-occupancy"
              >
                {{this.occupancyDifference.formatted}}
              </td>
            {{/if}}
          </tr>
        </tbody>
      </table>
    </div>
  </template>
}
